import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Map, Calendar, MoreVertical, Trash2, Globe, Lock, BarChart3, Target, TrendingUp } from 'lucide-react';
import api from '../api';

function Dashboard({ user, onLogout }) {
  const [roadmaps, setRoadmaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newRoadmapName, setNewRoadmapName] = useState('');
  const [newRoadmapDesc, setNewRoadmapDesc] = useState('');
  const [goals, setGoals] = useState([]);
  const [releases, setReleases] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [roadmapData, goalsData, releasesData] = await Promise.all([
        api.getRoadmaps(),
        api.getGoals().catch(() => []),
        api.getReleases().catch(() => []),
      ]);
      setRoadmaps(roadmapData);
      setGoals(goalsData);
      setReleases(releasesData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRoadmap = async (e) => {
    e.preventDefault();
    try {
      const newRoadmap = await api.createRoadmap({
        name: newRoadmapName,
        description: newRoadmapDesc,
        visibility: 'private',
      });
      setRoadmaps([...roadmaps, newRoadmap]);
      setShowCreateModal(false);
      setNewRoadmapName('');
      setNewRoadmapDesc('');
      navigate(`/roadmap/${newRoadmap._id}`);
    } catch (error) {
      console.error('Failed to create roadmap:', error);
    }
  };

  const handleDeleteRoadmap = async (e, roadmapId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this roadmap? This action cannot be undone.')) return;
    try {
      await api.deleteRoadmap(roadmapId);
      setRoadmaps(roadmaps.filter(r => r._id !== roadmapId));
    } catch (error) {
      console.error('Failed to delete roadmap:', error);
    }
  };

  return (
    <div className="min-h-full" data-testid="dashboard-page">
      {/* Page header */}
      <div className="px-6 lg:px-8 pt-8 pb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="dashboard-title">Roadmaps</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage and visualize your product roadmaps</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-5 py-2.5 bg-primary text-primary-foreground rounded-xl hover:opacity-90 font-semibold flex items-center gap-2 shadow-sm shadow-primary/20 btn-press transition-opacity"
            data-testid="create-roadmap-button"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">New Roadmap</span>
            <span className="sm:hidden">New</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 lg:px-8 pb-8">
        {/* Analytics overview */}
        {!loading && roadmaps.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-testid="dashboard-analytics">
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Map className="h-4 w-4 text-primary" />
                <p className="text-xs text-muted-foreground">Roadmaps</p>
              </div>
              <p className="text-2xl font-bold font-mono-score">{roadmaps.length}</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Target className="h-4 w-4 text-amber-500" />
                <p className="text-xs text-muted-foreground">Active Goals</p>
              </div>
              <p className="text-2xl font-bold font-mono-score">{goals.filter(g => g.status === 'active').length}</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-4 w-4 text-blue-500" />
                <p className="text-xs text-muted-foreground">Releases</p>
              </div>
              <p className="text-2xl font-bold font-mono-score">{releases.length}</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-4 cursor-pointer card-hover" onClick={() => navigate('/capacity')}>
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="h-4 w-4 text-green-500" />
                <p className="text-xs text-muted-foreground">Capacity</p>
              </div>
              <p className="text-sm font-medium text-primary">View Workload &rarr;</p>
            </div>
          </div>
        )}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-[180px] skeleton rounded-xl"></div>
            ))}
          </div>
        ) : roadmaps.length === 0 ? (
          <div className="text-center py-20 px-6">
            <div className="w-16 h-16 mx-auto mb-5 bg-muted rounded-2xl flex items-center justify-center">
              <Map className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No roadmaps yet</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">Create your first roadmap to start planning your product journey</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 bg-primary text-primary-foreground rounded-xl hover:opacity-90 font-semibold inline-flex items-center gap-2 shadow-sm shadow-primary/20 btn-press transition-opacity"
            >
              <Plus className="h-4 w-4" /> Create Roadmap
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {roadmaps.map((roadmap) => (
              <div
                key={roadmap._id}
                onClick={() => navigate(`/roadmap/${roadmap._id}`)}
                className="group relative p-6 bg-card border border-border rounded-xl cursor-pointer card-hover"
                data-testid={`roadmap-card-${roadmap._id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Map className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground leading-tight">{roadmap.name}</h3>
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleDeleteRoadmap(e, roadmap._id)}
                    className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-destructive/10 text-muted-foreground hover:text-destructive rounded-lg transition-opacity"
                    data-testid={`delete-roadmap-${roadmap._id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                <p className="text-sm text-muted-foreground mb-5 line-clamp-2 min-h-[40px]">
                  {roadmap.description || 'No description'}
                </p>

                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5" />
                    <span>Updated {new Date(roadmap.updated_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {roadmap.visibility === 'private' ? (
                      <><Lock className="h-3.5 w-3.5" /> Private</>
                    ) : (
                      <><Globe className="h-3.5 w-3.5" /> Public</>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Roadmap Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="create-modal">
          <div className="bg-card p-6 rounded-xl max-w-md w-full border border-border shadow-2xl">
            <h2 className="text-xl font-bold mb-1">Create New Roadmap</h2>
            <p className="text-sm text-muted-foreground mb-6">Set up a new roadmap for your team</p>
            <form onSubmit={handleCreateRoadmap} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-2">
                  Name *
                </label>
                <input
                  id="name"
                  type="text"
                  value={newRoadmapName}
                  onChange={(e) => setNewRoadmapName(e.target.value)}
                  required
                  placeholder="e.g., Q3 2026 Product Roadmap"
                  className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="roadmap-name-input"
                />
              </div>
              <div>
                <label htmlFor="description" className="block text-sm font-medium mb-2">
                  Description
                </label>
                <textarea
                  id="description"
                  value={newRoadmapDesc}
                  onChange={(e) => setNewRoadmapDesc(e.target.value)}
                  rows={3}
                  placeholder="Brief description of this roadmap's purpose"
                  className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="roadmap-desc-input"
                />
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-5 py-2.5 bg-secondary text-secondary-foreground rounded-xl hover:bg-secondary/80 font-medium transition-colors"
                  data-testid="cancel-button"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-primary text-primary-foreground rounded-xl hover:opacity-90 font-semibold shadow-sm shadow-primary/20 btn-press transition-opacity"
                  data-testid="create-submit-button"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
